import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { UserPlus, Clock, MessageSquare, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

/**
 * Smart button shown next to a member.
 * - Renders nothing for self.
 * - "Connect" when no relationship exists.
 * - "Pending" when an outgoing request was sent.
 * - "Accept" when an incoming request is waiting.
 * - "Message" when accepted (or admin / grandfathered).
 *
 * Props:
 *   userId: target user id
 *   size: button size
 *   className: extra classes
 *   variant: full | compact (compact = icon-only on small screens)
 */
const ConnectButton = ({ userId, size = 'sm', className = '', onChange }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null); // {status, can_message, connection_id}
  const [busy, setBusy] = useState(false);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get(`/connections/status/${userId}`);
      setStatus(data);
    } catch (e) {
      // Silently no-op; button just won't render
    }
  };

  useEffect(() => {
    if (userId) fetchStatus();
  }, [userId]);

  if (!status || status.status === 'self') return null;

  const goToMessages = (e) => {
    e?.stopPropagation();
    navigate('/messages', { state: { partnerId: userId } });
  };

  const handleConnect = async (e) => {
    e?.stopPropagation();
    setBusy(true);
    try {
      await api.post(`/connections/request/${userId}`);
      toast.success('Connection request sent');
      await fetchStatus();
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    } finally {
      setBusy(false);
    }
  };

  const handleAccept = async (e) => {
    e?.stopPropagation();
    if (!status.connection_id) return;
    setBusy(true);
    try {
      await api.post(`/connections/${status.connection_id}/accept`);
      toast.success('Connection accepted');
      await fetchStatus();
      onChange?.();
    } catch (err) {
      toast.error('Failed to accept');
    } finally {
      setBusy(false);
    }
  };

  // Accepted, or admin/grandfathered → message directly
  if (status.status === 'accepted' || (status.can_message && status.status === 'none')) {
    return (
      <Button
        size={size}
        onClick={goToMessages}
        className={`bg-[#22C55E] hover:bg-[#16A34A] text-white ${className}`}
        data-testid="message-button"
      >
        <MessageSquare className="h-4 w-4 mr-2" />
        Message
      </Button>
    );
  }

  if (status.status === 'pending_outgoing') {
    return (
      <Button
        size={size}
        disabled
        variant="outline"
        className={`text-slate-500 ${className}`}
        data-testid="pending-button"
      >
        <Clock className="h-4 w-4 mr-2" />
        Pending
      </Button>
    );
  }

  if (status.status === 'pending_incoming') {
    return (
      <Button
        size={size}
        onClick={handleAccept}
        disabled={busy}
        className={`bg-[#1E3A8A] hover:bg-[#172554] text-white ${className}`}
        data-testid="accept-connection-button"
      >
        <Check className="h-4 w-4 mr-2" />
        Accept
      </Button>
    );
  }

  // Default: no relationship → Connect
  return (
    <Button
      size={size}
      onClick={handleConnect}
      disabled={busy}
      className={`bg-[#1E3A8A] hover:bg-[#172554] text-white ${className}`}
      data-testid="connect-button"
    >
      <UserPlus className="h-4 w-4 mr-2" />
      Connect
    </Button>
  );
};

export default ConnectButton;
